import React, { useState, useRef, useEffect } from 'react';
import { Loading } from './ui/Loading';
import { Message as MessageComponent } from './Message';
import type { Message, Model, Conversation } from '../types';

export interface ChatInterfaceProps {
  conversationId: string;
  messages: Message[];
  currentModelId: string;
  isStreaming: boolean;
  onSendMessage: (content: string) => void;
  onCancelStream?: () => void;
  models?: Model[];
  onBranchFromSelection?: (messageId: string, excerpt: string) => void;
  subchatsByMessageId?: Map<string, Conversation[]>;
  onOpenSubchat?: (subchat: Conversation) => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isStreaming,
  onSendMessage,
  onCancelStream,
  models = [],
  onBranchFromSelection,
  subchatsByMessageId,
  onOpenSubchat,
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const getModelName = (modelId: string): string => {
    if (!modelId) return 'Unknown Model';
    const model = models.find(m => m.id === modelId);
    if (model) return model.name;
    return modelId
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getModelProvider = (modelId: string): string | undefined => {
    return models.find(m => m.id === modelId)?.provider;
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = inputValue.trim();
    if (!trimmedValue || isStreaming) return;
    onSendMessage(trimmedValue);
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Send a message to start the conversation
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-4 py-6 space-y-1">
            {messages.map((message) => (
              <MessageComponent
                key={message.id}
                message={message}
                modelName={message.role === 'assistant' ? getModelName(message.modelId) : undefined}
                provider={message.role === 'assistant' ? getModelProvider(message.modelId) : undefined}
                onBranchFromSelection={message.role === 'assistant' ? onBranchFromSelection : undefined}
                subchats={message.role === 'assistant' ? subchatsByMessageId?.get(message.id) : undefined}
                onOpenSubchat={onOpenSubchat}
              />
            ))}

            {/* Streaming indicator */}
            {isStreaming && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start py-2">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 shadow-sm">
                  <Loading size="sm" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex items-end gap-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl p-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                disabled={isStreaming}
                rows={1}
                className="flex-1 bg-transparent border-none outline-none resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 px-2 py-1.5 text-sm"
                style={{ minHeight: '36px', maxHeight: '200px' }}
              />

              <div className="flex items-center gap-1 pb-0.5">
                {isStreaming && onCancelStream ? (
                  <button
                    type="button"
                    onClick={onCancelStream}
                    className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                    title="Cancel"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isStreaming}
                    className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white transition-colors"
                    title="Send (Enter)"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </form>

          <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500 text-center">
            Enter to send · Shift+Enter for new line · Files in your library are always available as context
          </p>
        </div>
      </div>
    </div>
  );
};
